import { BalanceData, Fix64, LevelSim } from "@highmarch/domain";
import { tierFourArt } from "./portraits";
import { btnHtml, ribbonHtml } from "./kit";

/**
 * The branch picker (M21, docs/TOWERS_V2.md "Tier 4"): picking an Ascend card opens two big branch cards over the
 * battle — art, name, identity line, what tier IV changes and both skills — and the player chooses one for the run.
 * A tap selects a branch; ASCEND confirms (a run-long choice should not hang on one stray tap); BACK returns the card.
 * The model and markup are pure (tested in packages/web/test/ascendUi.test.ts); `BranchPicker` only wires the DOM.
 */

export interface BranchSkillPreview { readonly letter: "A" | "B"; readonly name: string; readonly description: string }
export interface BranchChoice {
  readonly slot: number;
  readonly id: string;
  readonly name: string;
  readonly identity: string;
  readonly towerId: string;
  /** What tier IV changes against tier III, as short chips ("DPS ×1.6", "RANGE +0.25"). */
  readonly chips: readonly string[];
  readonly skills: readonly BranchSkillPreview[];
}

const PERCENT_EPS = 0.005;
const fmt = (x: number) => (Math.abs(x - Math.round(x)) < PERCENT_EPS ? String(Math.round(x)) : x.toFixed(2).replace(/0$/, ""));

/** The tower type's two branches as the picker shows them. */
export function branchChoices(B: BalanceData, archetype: number): BranchChoice[] {
  const a = B.towers[archetype];
  const t3 = a.tiers[a.tiers.length - 1];
  return (B.branches.byTower[archetype] ?? []).map((i) => {
    const b = B.branches.branches[i];
    const chips: string[] = [];
    const dm = b.tier4.num("damageMult", t3.damageMult).toNumber() / Math.max(1e-9, t3.damageMult.toNumber());
    const rate = 1 / b.tier4.num("fireIntervalMult", Fix64.One).toNumber();
    const range = b.tier4.num("rangeBonus", t3.rangeBonus).toNumber() - t3.rangeBonus.toNumber();
    // damage and fire rate as one number (a Ranger's lighter, faster arrows are more damage, not less); none for a
    // shot the branch replaces (the Tesla's charge) or a tower without one
    // a salvo or a second bolt is more shots a turn: the Rocket Mortar's three shells, the Twin Repeater's two bolts
    // (and the Sorcerer's split beams: each beam is a full one)
    const shots = Math.max(1, b.tier4.int("shells", 1)) * Math.max(1, b.tier4.int("bolts", 1)) * Math.max(1, b.tier4.int("beams", 1));
    const dps = dm * shots * (b.tier4.has("chargeTicks") ? 1 : rate);
    // Only a gain is shown: a branch whose single shot hits for less (the Inferno's cone, the Phoenix Roost) makes it up
    // in the shape its identity line names, so "DPS ×0.48" would read as a nerf it is not (M21 integration).
    if (a.damage.isPositive() && a.fireIntervalTicks > 0 && !b.tier4.has("chargeTicks") && dps - 1 > PERCENT_EPS) chips.push(`DPS ×${fmt(dps)}`);
    if (range > PERCENT_EPS) chips.push(`RANGE +${fmt(range)}`);
    return {
      slot: b.slot, id: b.id, name: b.name, identity: b.identity, towerId: b.towerId, chips,
      skills: b.skills.map((s, k): BranchSkillPreview => ({ letter: k === 0 ? "A" : "B", name: s.name, description: s.description })),
    };
  });
}

/** Tier-3 towers of the type standing now: the ones the choice lifts at once. */
export function towersRaised(sim: LevelSim, archetype: number): number {
  return sim.towerSlots.filter((t) => t.alive && t.archetype === archetype && t.tier === 3).length;
}

/** The picker's markup: `selected` is the slot chosen so far (−1: none yet, ASCEND is off). */
export function branchPickerHtml(B: BalanceData, archetype: number, raised: number, selected: number): string {
  const a = B.towers[archetype];
  const cards = branchChoices(B, archetype).map((c) => `
    <button class="bcard${c.slot === selected ? " on" : ""}" data-slot="${c.slot}" aria-pressed="${c.slot === selected}">
      <div class="bart">${tierFourArt(c.towerId, c.id, 96)}</div>
      <b class="disp bname">${c.name}</b>
      <p class="bid">${c.identity}</p>
      ${c.chips.length ? `<div class="bchips">${c.chips.map((x) => `<i>${x}</i>`).join("")}</div>` : ""}
      <ul class="bskills">${c.skills.map((s) => `<li><i class="disp">${s.letter}</i><span><b>${s.name}</b><small>${s.description}</small></span></li>`).join("")}</ul>
    </button>`).join("");
  const who = raised === 1 ? `Your tier-3 ${a.name} rises` : raised === 2 ? `Both tier-3 ${a.name}s rise` : `All ${raised} tier-3 ${a.name}s rise`;
  const lede = raised > 0 ? `${who} to tier IV now, the rest with Upgrade cards.` : `${a.name}s reach tier IV with an Upgrade card.`;
  const chosen = selected >= 0 ? branchChoices(B, archetype).find((c) => c.slot === selected)?.name ?? "" : "";
  return `<div class="panel bpanel">${ribbonHtml(`Ascend: ${a.name}`, "", "btitle")}
    <p class="blede">${lede} Choose its path for this battle: skills arrive on later cards.</p>
    <div class="bcards">${cards}</div>
    <div class="row two">${btnHtml("BACK", "dark", { className: "bback" })}${btnHtml(chosen ? "ASCEND" : "PICK A BRANCH", "gold", { className: `bgo accent${chosen ? " ready" : ""}`, disabled: !chosen, sub: chosen || undefined })}</div>
  </div>`;
}

/** The overlay: shown while an Ascend card is picked, gone otherwise. */
export class BranchPicker {
  readonly root: HTMLDivElement;
  private archetype = -1;
  private selected = -1;
  private key = "";

  constructor(parent: HTMLElement, private readonly balance: BalanceData, private readonly onChoose: (slot: number) => void, private readonly onBack: () => void) {
    this.root = document.createElement("div");
    this.root.id = "branch-picker";
    this.root.className = "scrim bpick hidden";
    parent.append(this.root);
  }

  get open(): boolean { return this.archetype >= 0; }

  /** Shows the picker for `archetype` (a picked Ascend card), or hides it with `archetype` −1. */
  show(sim: LevelSim, archetype: number): void {
    if (archetype !== this.archetype) this.selected = -1;
    this.archetype = archetype;
    this.root.classList.toggle("hidden", archetype < 0);
    if (archetype < 0) { this.key = ""; return; }
    const raised = towersRaised(sim, archetype);
    const key = `${archetype}|${raised}|${this.selected}`;
    if (key === this.key) return;
    this.key = key;
    this.root.innerHTML = branchPickerHtml(this.balance, archetype, raised, this.selected);
    for (const b of this.root.querySelectorAll<HTMLButtonElement>(".bcard")) b.onclick = () => { this.selected = Number(b.dataset.slot); this.show(sim, archetype); };
    this.root.querySelector<HTMLButtonElement>(".bback")!.onclick = () => this.onBack();
    const go = this.root.querySelector<HTMLButtonElement>(".bgo")!;
    go.onclick = () => { if (this.selected >= 0) this.onChoose(this.selected); };
  }

  dispose(): void { this.root.remove(); }
}
